/**
 * Relationship identifier — ARCHITECTURE.md section 6.3.
 *
 * Uses Sonnet via the LLM adapter to identify typed, directional
 * relationships between extracted entities based on context text.
 *
 * Pipeline position: after entity extraction, before deterministic validation.
 *
 * Key behaviors:
 * - Only uses predefined relationship types from desk ontology
 * - Evidence quotes must exist verbatim in context text
 * - Relationships with unknown entity names are rejected
 */

import type { EntityType, RelationshipType } from '@/intelligence/entities/types';
import type { OntologyExtensions } from '@/config/desk-config';
import type {
  LLMAdapter,
  RawExtractedRelationship,
  LLMCallResult,
} from './llm-adapter';

// ---------------------------------------------------------------------------
// Default relationship types (ARCHITECTURE.md section 3.2)
// ---------------------------------------------------------------------------

const CORE_RELATIONSHIP_TYPES: RelationshipType[] = [
  'commands', 'based_at', 'deployed_to', 'affiliated_with',
  'participated_in', 'observed_at', 'reports', 'corroborates',
  'owns', 'operates', 'employs', 'member_of', 'located_at',
  'subsidiary_of', 'sanctioned_by', 'associated_with',
  'traveled_to', 'communicated_with',
];

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

/** A relationship that passed initial checks */
export interface IdentifiedRelationship {
  sourceEntity: string;
  targetEntity: string;
  type: RelationshipType;
  confidence: number;
  evidenceQuote: string;
  grounded: boolean;
}

export interface RelationshipIdentificationResult {
  /** Relationships that passed initial checks */
  identified: IdentifiedRelationship[];
  /** Relationships rejected with reasons */
  rejected: Array<{ relationship: RawExtractedRelationship; reason: string }>;
  /** Token usage for this call */
  tokensUsed: number;
  /** Model used */
  model: string;
}

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

export interface RelationshipIdentifier {
  /**
   * Identify relationships between known entities based on context text.
   * @param entities - Known entities (name + type) from the extraction step
   * @param context - Source text providing evidence for relationships
   * @param ontologyExtensions - Optional custom relationship types from desk config
   */
  identify(
    entities: Array<{ name: string; type: EntityType }>,
    context: string,
    ontologyExtensions?: OntologyExtensions,
  ): Promise<RelationshipIdentificationResult>;
}

// ---------------------------------------------------------------------------
// Grounding check
// ---------------------------------------------------------------------------

function checkEvidenceGrounding(quote: string, contextText: string): boolean {
  if (!quote || quote.length < 3) return false;
  const normalizeWs = (s: string) => s.replace(/\s+/g, ' ').trim();
  const normalizedQuote = normalizeWs(quote).toLowerCase();
  const normalizedContext = normalizeWs(contextText).toLowerCase();
  return normalizedContext.includes(normalizedQuote);
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

/**
 * Create a relationship identifier for an investigation.
 * @param adapter - LLM adapter instance (with its own token tracker)
 */
export function createRelationshipIdentifier(adapter: LLMAdapter): RelationshipIdentifier {
  return {
    async identify(entities, context, ontologyExtensions) {
      // Build allowed relationship types from core + custom
      const allowedTypes: RelationshipType[] = [...CORE_RELATIONSHIP_TYPES];
      if (ontologyExtensions?.customRelationships) {
        for (const custom of ontologyExtensions.customRelationships) {
          if (custom.type && !allowedTypes.includes(custom.type as RelationshipType)) {
            allowedTypes.push(custom.type as RelationshipType);
          }
        }
      }

      // Build entity name set for validation
      const entityNames = new Set(entities.map(e => e.name.toLowerCase()));

      // Call LLM via adapter
      const result: LLMCallResult<{ relationships: RawExtractedRelationship[] }> =
        await adapter.identifyRelationships(entities, context, allowedTypes);

      const rawRelationships = result.data.relationships ?? [];
      const identified: IdentifiedRelationship[] = [];
      const rejected: Array<{ relationship: RawExtractedRelationship; reason: string }> = [];

      for (const raw of rawRelationships) {
        // Check required fields
        if (!raw.sourceEntity || !raw.targetEntity) {
          rejected.push({
            relationship: raw,
            reason: 'Missing required field: sourceEntity or targetEntity',
          });
          continue;
        }

        if (!raw.type) {
          rejected.push({ relationship: raw, reason: 'Missing required field: type' });
          continue;
        }

        // Check relationship type is allowed
        if (!allowedTypes.includes(raw.type)) {
          rejected.push({
            relationship: raw,
            reason: `Relationship type "${raw.type}" not in allowed types`,
          });
          continue;
        }

        // Check entity names are known
        if (!entityNames.has(raw.sourceEntity.toLowerCase())) {
          rejected.push({
            relationship: raw,
            reason: `Source entity "${raw.sourceEntity}" not found in known entities`,
          });
          continue;
        }

        if (!entityNames.has(raw.targetEntity.toLowerCase())) {
          rejected.push({
            relationship: raw,
            reason: `Target entity "${raw.targetEntity}" not found in known entities`,
          });
          continue;
        }

        // Self-referential check
        if (raw.sourceEntity.toLowerCase() === raw.targetEntity.toLowerCase()) {
          rejected.push({
            relationship: raw,
            reason: 'Self-referential relationship: source and target are the same entity',
          });
          continue;
        }

        // Confidence check
        if (typeof raw.confidence !== 'number' || raw.confidence < 0 || raw.confidence > 1) {
          rejected.push({
            relationship: raw,
            reason: `Invalid confidence value: ${raw.confidence} (must be 0-1)`,
          });
          continue;
        }

        // Evidence quote check
        if (!raw.evidenceQuote || raw.evidenceQuote.trim().length === 0) {
          rejected.push({
            relationship: raw,
            reason: 'Missing required field: evidenceQuote',
          });
          continue;
        }

        // Grounding check
        const grounded = checkEvidenceGrounding(raw.evidenceQuote, context);

        if (!grounded) {
          rejected.push({
            relationship: raw,
            reason: 'Grounding check failed: evidenceQuote not found verbatim in context text',
          });
          continue;
        }

        identified.push({
          sourceEntity: raw.sourceEntity,
          targetEntity: raw.targetEntity,
          type: raw.type,
          confidence: raw.confidence,
          evidenceQuote: raw.evidenceQuote,
          grounded: true,
        });
      }

      return {
        identified,
        rejected,
        tokensUsed: result.tokensUsed,
        model: result.model,
      };
    },
  };
}
